import { memo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CalendarDays, MapPin, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatInCentralTime } from "@/utils/timezone";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useGuest } from "@/contexts/GuestContext";

const FOUNDER_USERNAME = "Thewellnesscowgirl";

export const FounderEventsSection = memo(function FounderEventsSection() {
  const navigate = useNavigate();
  const { isGuest } = useGuest();
  const { data: events = [], isLoading } = useQuery({
    queryKey: ["founder-upcoming-events"],
    queryFn: async () => {
      const { data: founder, error: founderError } = await supabase
        .from("profiles")
        .select("id")
        .eq("username", FOUNDER_USERNAME)
        .maybeSingle();
      if (founderError) throw founderError;
      if (!founder) return [];
      const { data, error } = await supabase
        .from("event_attendances")
        .select("event_id, events!inner(id, title, date, location, host_logo_url, category, is_cowgirls_hosted)")
        .eq("user_id", founder.id)
        .eq("status", "rsvp")
        .gte("events.date", new Date().toISOString())
        .order("events(date)", { ascending: true })
        .limit(6);
      if (error) throw error;
      return (data ?? []).map((row) => row.events);
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isGuest || (!isLoading && events.length === 0)) return null;

  return (
    <section aria-labelledby="going-with-megan-title">
      <p className="eyebrow mb-1">A familiar face</p>
      <h2 id="going-with-megan-title" className="font-display text-xl font-semibold text-foreground">Going with Megan</h2>
      <p className="mt-1 text-sm text-muted-foreground">Want a familiar face? Megan is planning to be at these events.</p>
      <div className="mt-4 flex snap-x gap-3 overflow-x-auto pb-2">
        {isLoading ? [0, 1].map((item) => <Skeleton key={item} className="h-52 w-64 shrink-0 rounded-3xl" />) : events.map((event) => (
          <Card key={event.id} className="w-64 shrink-0 snap-start overflow-hidden rounded-3xl border-primary/15 shadow-soft">
            <div className="h-24 overflow-hidden bg-muted">
              {event.host_logo_url && <img src={event.host_logo_url} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="p-4">
              <p className="mb-2 flex items-center gap-1 text-xs font-semibold text-primary"><Sparkles className="h-3.5 w-3.5" /> Megan is going</p>
              <h3 className="line-clamp-2 font-display font-semibold text-foreground">{event.title}</h3>
              <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><CalendarDays className="h-3.5 w-3.5" />{formatInCentralTime(event.date, "EEE, MMM d · h:mm a")}</p>
              <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" /><span className="line-clamp-1">{event.location}</span></p>
              <Button className="mt-3 h-11 w-full" variant="outline" onClick={() => navigate(`/events/${event.id}`)}>View event</Button>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
});